// angular dependencies
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';

// third party dependencies
import { UntilDestroy } from '@ngneat/until-destroy';
import * as Hammer from 'hammerjs';
import { default as slugify } from 'slugify';

// shared dependencies
import {
  CasualTournamentEntity,
  Game,
} from '@fantaz/shared/data-access/models';
import { CurrencyService } from '@fantaz/shared/utils';

interface TournamentPriceRange {
  start: number;
  end: number;
}

@UntilDestroy()
@Component({
  selector: 'fz-game-card',
  templateUrl: './game-card.component.html',
  styleUrls: ['./game-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GameCardComponent implements OnDestroy, AfterViewInit {
  // inject dependencies
  private renderer: Renderer2 = inject(Renderer2);
  private elementRef: ElementRef<HTMLDivElement> = inject(ElementRef);
  private readonly changeDetectorRef: ChangeDetectorRef =
    inject(ChangeDetectorRef);
  #currencyService: CurrencyService = inject(CurrencyService);
  // Identifier
  @Input()
  tournament?: CasualTournamentEntity;

  @Input()
  isEvent = false;

  @Input()
  expandOnHover = true;

  @Input()
  disableExtraData?: boolean;

  @Input()
  cardRightTag?: TemplateRef<any>;

  @Input()
  opened?: boolean;

  @Input()
  hovered?: boolean;

  @Input()
  assetUrl? = '';

  @Output()
  selected = new EventEmitter();

  @Output()
  share = new EventEmitter();

  @Output()
  previewPress = new EventEmitter();

  // Range of price
  @Input() priceRange: TournamentPriceRange = {
    start: 50.0,
    end: 100.0,
  };

  @ViewChild('cardElement', { read: ElementRef })
  cardEl: ElementRef;

  hammer: HammerManager;

  get actionText(): string {
    if (!this.tournament) {
      return 'Join Now ';
    }

    if (this.tournament.isComplete) {
      return 'Completed - see details';
    }

    if (this.tournament.isComplete || this.tournament.ended) {
      return 'Ended - See detail';
    }

    if (this.tournament.comingSoon) {
      return 'Coming Soon - Subscribe';
    }

    if (!this.tournament.lifeExperience) {
      if (
        this.tournament?.casualGameStartTime &&
        this.tournament?.casualGameEndTime instanceof Date
      ) {
        if (this.tournament?.casualGameStartTime?.getTime() > Date.now())
          return 'Coming soon - Details';
      }
      if (
        this.tournament?.casualGameStartTime &&
        this.tournament?.casualGameEndTime instanceof String
      ) {
        if (
          new Date(this.tournament?.casualGameStartTime).getTime() > Date.now()
        )
          return 'Coming soon - Details';
      }

      if (
        this.tournament?.casualGameEndTime &&
        this.tournament?.casualGameEndTime instanceof Date
      ) {
        if (this.tournament?.casualGameEndTime?.getTime() <= Date.now()) {
          return 'Ended - See detail';
        }
      }

      if (
        this.tournament?.casualGameEndTime &&
        this.tournament?.casualGameEndTime instanceof String
      ) {
        if (
          new Date(this.tournament?.casualGameEndTime)?.getTime() <= Date.now()
        ) {
          return 'Ended - See details';
        }
      }
    }

    // format price
    if (this.tournament.lifeExperience) {
      const price = this.#currencyService.convertDollar(
        this.tournament.eventSchedules[0].entryAmount,
      );
      return `Join Now - ${price} USD`;
    } else {
      const price = this.#currencyService.convertDollar(
        this.tournament?.entryFeeRange?.start,
      );
      return `Join Now - From ${price} USD`;
    }
  }

  ngAfterViewInit() {
    // subscribe to event handlers only on mobile and tablet
    this.hammer = new Hammer(this.elementRef.nativeElement, {
      // domEvents: true,
    });

    // add recognizer events for tap, press, swipe
    const tap = new Hammer.Tap({
      event: 'tap',
      taps: 1,
      velocity: 0.3,
    });
    const longPress = new Hammer.Press({
      event: 'longpress',
      time: 500,
    });

    // only add long press event on mobile and tablet
    this.hammer.add([longPress, tap]);

    // Subscribe to desired event
    if (window.innerWidth <= 1024) {
      this.hammer.on('longpress', (ev) => {
        this.onOpenPreview(ev);
      });
    }

    this.hammer.on('tap', (event) => {
      // ignore tap events for touchable area
      if (
        event.target.classList.contains('touchable-area') ||
        event.target.classList.contains('mat-mdc-tooltip-trigger')
      ) {
        return;
      }

      // call card on tap event
      this.handleTapCard();
    });

    if (this.tournament && this.tournament?.name.length > 15) {
      this.renderer.addClass(this.cardEl.nativeElement, 'title-long-name');
    }
  }

  ngOnDestroy() {
    // remove event handlers
    if (this.hammer) {
      this.hammer.destroy();
    }
  }

  getGameImage(
    game: Game,
    type: 'avatar' | 'card' | 'background' = 'avatar',
  ): string {
    const result = game.key.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    if (type === 'background') {
      return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-card.webp`;
    }

    return `${this.assetUrl}/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  get gameName(): string {
    let name = this.tournament?.game.name as string;
    name = 'game-' + name.toLowerCase();
    return slugify(name);
  }

  get getGameImageUrl(): string {
    return this.getGameImage(this.tournament?.game as Game);
  }

  get getBackgroundImageUrl(): string {
    if (this.tournament?.metadata?.cardImage) {
      return this.tournament.metadata.cardImage.url;
    }

    if (
      this.tournament?.lifeExperience &&
      this.tournament.tournamentCardBackground
    ) {
      return this.tournament.tournamentCardBackground as string;
    }
    return this.getGameImage(this.tournament?.game as Game, 'card');
  }

  get isExtended(): boolean {
    if (this.tournament?.metadata?.extended) {
      const value = JSON.parse(
        (this.tournament.metadata.extended as string) || 'false',
      );
      return value as boolean;
    }
    return false;
  }

  handleTapCard() {
    this.selected.emit(this.tournament);
    this.onClosePreview(null);
  }

  handleShare(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.share.emit(event);
  }

  onOpenPreview(source: any) {
    this.previewPress.emit({
      ...source,
      target: this.cardEl.nativeElement,
    });
  }

  onClosePreview(event: any) {
    this.previewPress.emit(null);
  }
}
